"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { motion, AnimatePresence } from "framer-motion"
import { Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"
import { Icon } from "@/shared/components/icons"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"

import { forgotPasswordSchema, type ForgotPasswordInput } from "@/features/auth/validations"
import { forgotPassword } from "@/features/auth/actions"
import { track } from "@/shared/lib/analytics"
import { ROUTES } from "@/shared/lib/constants"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/shared/components/ui/card"

export function ForgotPasswordContent() {
  const t = useTranslations("auth.forgotPassword")
  const auth = useTranslations("auth.shared")
  const forms = useTranslations("forms")
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  })

  async function onSubmit(data: ForgotPasswordInput) {
    setIsLoading(true)
    setServerError(null)

    const result = await forgotPassword({ email: data.email })

    setIsLoading(false)

    if (result.error) {
      setServerError(result.error)
      return
    }

    track("auth:password_reset_request", {})
    setIsSuccess(true)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="mx-auto w-full max-w-[335px] sm:max-w-[390px]"
    >
      <Card className="glass border-border/50">
        <AnimatePresence mode="wait">
          {!isSuccess ? (
            <motion.div
              key="form"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <CardHeader className="text-center">
                <h1 className="text-2xl font-bold leading-none">
                  {t("title")}
                </h1>
                <CardDescription>
                  {t("description")}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {serverError && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-sm text-destructive">{serverError}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{auth("email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={forms("emailPlaceholder")}
                      autoComplete="email"
                      disabled={isLoading}
                      {...register("email")}
                      className={cn(
                        errors.email &&
                          "border-destructive focus-visible:ring-destructive/50"
                      )}
                    />
                    {errors.email && (
                      <p className="text-xs text-destructive">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-white text-black hover:bg-white/90 transition-opacity"
                    size="lg"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("sending")}
                      </>
                    ) : (
                      t("sendResetLink")
                    )}
                  </Button>
                </form>

                <div className="text-center">
                  <LocalizedLink
                    href={ROUTES.login}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="arrow-left" size={14} className="h-3.5 w-3.5" />
                    {auth("backToSignIn")}
                  </LocalizedLink>
                </div>
              </CardContent>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
                  <Icon name="message" size={32} className="h-8 w-8 text-foreground" />
                </div>
                <h1 className="text-2xl font-bold leading-none">
                  {auth("checkEmail")}
                </h1>
                <CardDescription>
                  {t("successDescription")}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3">
                  <Icon name="tick-circle" size={16} className="h-4 w-4 shrink-0 text-emerald-400" />
                  <p className="text-xs text-muted-foreground">
                    {auth("spamHint")}
                  </p>
                </div>

                <div className="text-center">
                  <LocalizedLink
                    href={ROUTES.login}
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="arrow-left" size={14} className="h-3.5 w-3.5" />
                    {auth("backToSignIn")}
                  </LocalizedLink>
                </div>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  )
}
